/**
 * Event-day reminder docs. Firestore is injected so tests pass fakes.
 * Live callers omit `deps` and use the app Firebase client.
 */

import { deleteDoc, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { getEventDisplayTitle } from '../../../services/instagram/instagramEventDisplay';
import {
  EVENT_DAY_REMINDERS_COLLECTION,
  eventDayKeyFromEvent,
  eventDayReminderId,
  planEventDayReminder,
  sarajevoDayKey,
} from './eventDayReminderPlan';

function liveDeps() {
  return { db, getDoc, setDoc, deleteDoc, serverTimestamp };
}

function reminderRef(deps, uid, eventId) {
  return doc(deps.db, EVENT_DAY_REMINDERS_COLLECTION, eventDayReminderId(uid, eventId));
}

function reminderPayload(uid, event, dayKey, deps) {
  return {
    uid,
    eventId: event.id,
    title: getEventDisplayTitle(event),
    location: String(event.location || '').trim(),
    dayKey,
    status: 'pending',
    updatedAt: deps.serverTimestamp(),
  };
}

export async function upsertEventDayReminder(uid, event, deps = liveDeps()) {
  if (!uid || !event?.id) return;
  const ref = reminderRef(deps, uid, event.id);
  const snap = await deps.getDoc(ref);
  const existing = snap.exists() ? snap.data() : null;
  const dayKey = eventDayKeyFromEvent(event);
  const action = planEventDayReminder({ dayKey, existing, todayKey: sarajevoDayKey() });
  if (action === 'skip') return;
  if (action === 'delete') {
    if (existing?.status === 'pending') await deps.deleteDoc(ref);
    return;
  }
  await deps.setDoc(ref, reminderPayload(uid, event, dayKey, deps), { merge: true });
}

export async function deleteEventDayReminder(uid, eventId, deps = liveDeps()) {
  if (!uid || !eventId) return;
  const ref = reminderRef(deps, uid, eventId);
  const snap = await deps.getDoc(ref);
  if (snap.exists() && snap.data()?.status === 'pending') {
    await deps.deleteDoc(ref);
  }
}

/** Create missing upcoming reminders. Does not rewrite docs the server already adjusted. */
export async function syncEventDayReminders(uid, events, deps = liveDeps()) {
  if (!uid || !Array.isArray(events) || events.length === 0) return;
  await Promise.all(events.filter((event) => event?.id).map(async (event) => {
    const ref = reminderRef(deps, uid, event.id);
    const snap = await deps.getDoc(ref);
    if (snap.exists()) return;
    const dayKey = eventDayKeyFromEvent(event);
    const action = planEventDayReminder({ dayKey, existing: null, todayKey: sarajevoDayKey() });
    if (action !== 'upsert') return;
    await deps.setDoc(ref, reminderPayload(uid, event, dayKey, deps));
  }));
}
